import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { AppAlertQueue } from '../utils/components/AppAlertQueue';
import { AlertEntry, AlertObject } from '../TypeScript/interfaces';

interface AppAlertContextType {
  appAlerts: AlertEntry[];
  addAppAlert(newAlert: AlertObject): string;
  updateAppAlert(id: string, update: Partial<AlertObject>): void;
  removeAppAlert(id: string): void;
  addAppAlertGroup(newAlert: AlertObject): void;
  removeAppAlertGroup(groupType: string): void;
}

const AppAlertContext = createContext<AppAlertContextType | undefined>(
  undefined,
);

export const AppAlertProvider = ({
  children,
}: PropsWithChildren): JSX.Element => {
  const [appAlerts, setAppAlerts] = useState<AlertEntry[]>([]);
  const appAlertIdRef = useRef<number>(0);

  /**
   * Adds an app alert to the snackbar to toast
   *
   * @param alert - The alert to add
   * @return The id of the alert
   */
  const addAppAlert = useCallback((newAlert: AlertObject): string => {
    const alertId = `app-alert-${appAlertIdRef.current}`;
    appAlertIdRef.current += 1;

    setAppAlerts((previousAlerts) => [
      ...previousAlerts,
      { ...newAlert, id: alertId },
    ]);

    return alertId;
  }, []);

  /**
   * Updates an existing alert without changing its position in the queue.
   *
   * @param id - Id of the alert to update
   * @param update - Alert properties to update
   */
  const updateAppAlert = useCallback(
    (id: string, update: Partial<AlertObject>) => {
      setAppAlerts((previousAlerts) =>
        previousAlerts.map((alert) =>
          alert.id === id ? { ...alert, ...update, id } : alert,
        ),
      );
    },
    [],
  );

  /**
   * Removes a specific alert from the snackbar
   *
   * @param id - Id of the alert to remove
   */
  const removeAppAlert = useCallback((id: string) => {
    setAppAlerts((previousAlerts) =>
      previousAlerts.filter((alert) => alert.id !== id),
    );
  }, []);

  /**
   * Adds an alert to a specific group
   * If an alert of the same group type already exists, it will be updated instead of adding a new one
   * Intended for managing similar alerts to avoid cluttering the snackbar
   *
   * @param newAlert - The alert to add or update in the group
   */
  const addAppAlertGroup = useCallback((newAlert: AlertObject) => {
    setAppAlerts((previousAlerts) => {
      const found = previousAlerts.find(
        (previous) => previous.groupType === newAlert.groupType,
      );
      if (found) {
        const update: AlertEntry = { ...found, ...newAlert, id: found.id };
        return previousAlerts.map((item) =>
          item.id === found.id ? update : item,
        );
      }

      const alertId = `app-alert-${appAlertIdRef.current}`;
      appAlertIdRef.current += 1;

      return [...previousAlerts, { ...newAlert, id: alertId }];
    });
  }, []);

  /**
   * Removes all alerts of a specific group type from the snackbar
   *
   * @param groupType - The type of the alert group to remove
   */
  const removeAppAlertGroup = useCallback((groupType: string) => {
    setAppAlerts((previousAlerts) =>
      previousAlerts.filter((alert) => alert.groupType !== groupType),
    );
  }, []);

  return (
    <AppAlertContext.Provider
      value={{
        appAlerts,
        addAppAlert,
        updateAppAlert,
        removeAppAlert,
        addAppAlertGroup,
        removeAppAlertGroup,
      }}
    >
      {children}
      <AppAlertQueue list={appAlerts} removeItem={removeAppAlert} />
    </AppAlertContext.Provider>
  );
};

export const useAppAlert = (): AppAlertContextType => {
  const context = useContext(AppAlertContext);
  if (!context) {
    throw new Error('useAppAlert must be used within an AppAlertProvider');
  }
  return context;
};
